import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { helixApi, queryKeys } from '@shared/api/helixApi';

type ErsaLanguage = 'en' | 'ar';
type ErsaCustomerType = 'bank' | 'insurance';

export function ErsaStartFlowButton() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [phoneInput, setPhoneInput] = useState('');
  const [formattedPhone, setFormattedPhone] = useState('');
  const [language, setLanguage] = useState<ErsaLanguage>('en');
  const [customerType, setCustomerType] = useState<ErsaCustomerType>('insurance');
  const [message, setMessage] = useState<{
    severity: 'error' | 'success' | 'warning';
    text: string;
  } | null>(null);
  const profiles = useQuery({
    queryKey: queryKeys.whatsAppProfiles,
    queryFn: helixApi.whatsAppProfiles,
    staleTime: 60_000,
  });
  const canStartErsa = profiles.data?.some((profile) => profile.id === 2) === true;

  const formatPhone = useMutation({
    mutationFn: () => helixApi.formatWhatsAppPhone({ phone: phoneInput, language }),
    onSuccess: (result) => {
      setFormattedPhone(result.formattedPhone);
      setMessage(null);
    },
    onError: () => {
      setFormattedPhone('');
      setMessage({ severity: 'error', text: t('ersa.invalidPhone') });
    },
  });
  const send = useMutation({
    mutationFn: (phone: string) =>
      helixApi.startErsaFlow({
        phone,
        ...(customerName.trim() ? { customerName: customerName.trim() } : {}),
        language,
        customerType,
      }),
    onSuccess: (result) => {
      setMessage({
        severity: result.flowStarted === false ? 'warning' : 'success',
        text: result.message,
      });
      setPhoneInput('');
      setFormattedPhone('');
      setCustomerName('');
    },
    onError: (error) => {
      setMessage({
        severity: 'error',
        text: error instanceof Error ? error.message : t('ersa.sendFailed'),
      });
    },
  });

  if (!canStartErsa) return null;

  const reset = () => {
    setCustomerName('');
    setPhoneInput('');
    setFormattedPhone('');
    setLanguage('en');
    setCustomerType('insurance');
    setMessage(null);
    formatPhone.reset();
    send.reset();
  };
  const normalizePhone = async (): Promise<string | null> => {
    if (!phoneInput.trim()) return null;
    if (formattedPhone) return formattedPhone;
    try {
      return (await formatPhone.mutateAsync()).formattedPhone;
    } catch {
      return null;
    }
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    const phone = await normalizePhone();
    if (phone) send.mutate(phone);
  };
  const busy = formatPhone.isPending || send.isPending;

  return (
    <>
      <Tooltip title={t('ersa.startFlow')}>
        <Button
          aria-label={t('ersa.startFlow')}
          variant="outlined"
          size="small"
          startIcon={<AddCommentOutlinedIcon />}
          onClick={() => setOpen(true)}
          sx={{
            borderColor: 'divider',
            color: 'text.primary',
            height: 36,
            minWidth: { xs: 36, xl: 'auto' },
            px: { xs: 0.75, xl: 1.25 },
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': { m: { xs: 0, xl: '0 8px 0 -4px' } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', xl: 'inline' } }}>
            {t('ersa.startFlow')}
          </Box>
        </Button>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => {
          if (busy) return;
          setOpen(false);
          reset();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 7 }}>
          {t('ersa.title')}
          <IconButton
            aria-label={t('common.close')}
            disabled={busy}
            onClick={() => {
              setOpen(false);
              reset();
            }}
            sx={{ position: 'absolute', insetInlineEnd: 12, top: 10 }}
          >
            <CloseRoundedIcon />
          </IconButton>
          <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
            {t('ersa.description')}
          </Typography>
        </DialogTitle>
        <Box component="form" onSubmit={(event) => void submit(event)}>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label={t('ersa.customerName')}
                value={customerName}
                onChange={(event) => setCustomerName(event.target.value)}
                slotProps={{ htmlInput: { maxLength: 200 } }}
                disabled={busy}
                fullWidth
              />
              <TextField
                label={t('ersa.mobile')}
                value={phoneInput}
                onChange={(event) => {
                  setPhoneInput(event.target.value);
                  setFormattedPhone('');
                  setMessage(null);
                }}
                onBlur={() => {
                  if (phoneInput.trim() && !formattedPhone && !formatPhone.isPending) {
                    void normalizePhone();
                  }
                }}
                placeholder="055 383 8888"
                required
                disabled={busy}
                fullWidth
              />
              <Box sx={{ bgcolor: 'background.default', borderRadius: 1.5, px: 1.5, py: 1.1 }}>
                <Typography color="text.secondary" component="span" sx={{ fontSize: 13 }}>
                  {t('ersa.whatsAppFormat')}:{' '}
                </Typography>
                <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>
                  {formatPhone.isPending ? t('ersa.formatting') : formattedPhone || '9715xxxxxxxx'}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('ersa.language')}</InputLabel>
                  <Select
                    label={t('ersa.language')}
                    value={language}
                    disabled={busy}
                    onChange={(event) => {
                      setLanguage(event.target.value);
                      setFormattedPhone('');
                    }}
                  >
                    <MenuItem value="en">{t('ersa.english')}</MenuItem>
                    <MenuItem value="ar">{t('ersa.arabic')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>{t('ersa.customerType')}</InputLabel>
                  <Select
                    label={t('ersa.customerType')}
                    value={customerType}
                    disabled={busy}
                    onChange={(event) => setCustomerType(event.target.value)}
                  >
                    <MenuItem value="insurance">{t('ersa.insurance')}</MenuItem>
                    <MenuItem value="bank">{t('ersa.bank')}</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Alert severity="info">
                {t('ersa.customerWillReceive', {
                  customerType: t(`ersa.${customerType}`),
                  language: t(`ersa.${language === 'en' ? 'english' : 'arabic'}`),
                })}
              </Alert>
              {message ? <Alert severity={message.severity}>{message.text}</Alert> : null}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={busy || !phoneInput.trim()}
              startIcon={send.isPending ? <CircularProgress color="inherit" size={16} /> : undefined}
            >
              {send.isPending ? t('ersa.sending') : t('ersa.send')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}
